/**
 * Everything the remote workbench knows about one AI Workbench, over the
 * relay. Each method is the workbench's own Control API (src/control/rpc.ts
 * there): the workbench checks scopes and policy and audits the call, so
 * this surface adds no second path into it. Views subscribe to what they
 * show; nothing polls for a view that is not open.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import { ApiError, routes } from '../api/client.ts';
import { useLoad } from '../hooks.ts';

export interface Workspace {
  id: string;
  name: string;
  root?: string;
  target?: string;
  focused?: boolean;
  /** Whether its host runs: `ready` or `stopped` as the app reports it; absent reads as ready. */
  state?: string;
  targetKind?: string;
  distro?: string;
  /** Set when the answer came from `projects.list`: the project and root this workspace is. */
  projectId?: string;
  rootId?: string;
}
/**
 * One of the machine's projects as the app has it (`projects.list`, a newer Control API method):
 * every project on the machine, open or not, with every root. No paths; the remote and branch
 * come from the platform's record of the root.
 */
export interface LiveProjectRoot {
  rootId: string;
  name?: string;
  /** "local", "wsl:<distro>", "ssh:<alias>", "devcontainer:…", "node:…". */
  target?: string;
  targetKind?: string;
  distro?: string;
  /** Absent when no host was started for it this run. */
  hostState?: 'ready' | 'starting' | 'provisioning' | 'stopped' | 'error' | 'exited' | string;
  targetState?: 'available' | 'stopped' | 'unavailable' | string;
  /** Present while a host runs: the id `workspaces.list` and the `workspace` parameter use. */
  workspaceId?: string;
}
export interface LiveProject {
  id: string;
  name: string;
  open: boolean;
  organisation?: boolean;
  roots: LiveProjectRoot[];
}

/**
 * `workspaces.list` as a flat array of workspaces, whatever shape the app answers: the flat array
 * of today, `{ workspaces }`, or `{ projects }` (whose roots with a running host are workspaces).
 */
export function workspaceList(raw: unknown): Workspace[] {
  if (Array.isArray(raw)) return raw as Workspace[];
  const r = (raw ?? {}) as { workspaces?: unknown; projects?: unknown };
  if (Array.isArray(r.workspaces)) return r.workspaces as Workspace[];
  if (Array.isArray(r.projects))
    return (r.projects as LiveProject[]).flatMap((p) =>
      (p.roots ?? [])
        .filter((x) => x.workspaceId)
        .map((x) => ({ id: x.workspaceId!, name: x.name ?? x.rootId, target: x.target, targetKind: x.targetKind, distro: x.distro, state: x.hostState, projectId: p.id, rootId: x.rootId, focused: p.open || undefined })),
    );
  return [];
}
export interface Status {
  workspace?: { id: string; name: string; root?: string };
  agents: number;
  working: number;
  pendingApprovals: number;
  teams: Array<{ id: string; name: string; agents: number }>;
  latestEvent: number;
}
export interface Session {
  sessionId: string;
  name: string;
  status: string;
  teamName?: string;
  provider?: string;
  model?: string;
  turnCount?: number;
  currentTool?: string;
  sessionTitle?: string;
  lastActivityAt?: string;
}
export interface Permission {
  permissionId: string;
  sessionId: string;
  agentName?: string;
  teamName?: string;
  title?: string;
  kind?: string;
  risk: 'low' | 'medium' | 'high';
  options?: Array<{ id?: string; name?: string; kind?: string }>;
}
export interface Decision {
  id: string;
  teamId: string;
  conversationId?: string;
  taskId?: string;
  askedBy: string;
  askedAt: string;
  question: string;
  why?: string;
  kind: 'single' | 'multi' | 'yesno' | 'text';
  options: Array<{ id: string; label: string; detail?: string }>;
  default?: string | string[];
  blocking: 'block' | 'proceed';
  status?: 'open' | 'answered';
  answer?: { value: string | string[]; by: string; at: string };
}
export interface ActivityAgent {
  sessionId: string;
  name: string;
  status: 'starting' | 'idle' | 'thinking' | 'tool_calling' | 'waiting_approval' | 'error' | string;
  teamId: string;
  provider?: string;
  model?: string;
  title?: string;
  currentTool?: string;
  isOpsManager?: boolean;
  lastActivityAt?: string;
  tasks: Array<{ id: string; title: string; status: string }>;
  permissions: Permission[];
  decisions: Decision[];
}
export interface Activity {
  at: number;
  agents: ActivityAgent[];
  decisions: Decision[];
  working: number;
  pendingApprovals: number;
  openDecisions: number;
}
export interface Team {
  id: string;
  name: string;
  running?: boolean;
  members?: string[];
}
export type TaskStatus = 'backlog' | 'todo' | 'in-progress' | 'review' | 'done' | 'blocked';
export interface Task {
  id: string;
  uid?: string;
  title: string;
  status: TaskStatus;
  priority: 'low' | 'medium' | 'high' | 'critical' | string;
  assignee?: string;
  labels: string[];
  dependencies?: string[];
  updatedAt: string;
}
export interface Plan {
  id: string;
  objective: string;
  summary?: string;
  status: 'draft' | 'approved' | 'dispatched' | 'done' | 'verified' | 'abandoned' | string;
  author: string;
  nodes: number;
  createdAt: string;
}
export interface Brief {
  id: string;
  title: string;
  type?: string;
  status: 'shaping' | 'ready' | 'launched' | 'abandoned' | string;
  gate: { result: 'pass' | 'warn' | 'fail'; missing: string[] };
  phases: number;
  conversationId?: string;
  updatedAt: string;
}
export interface RingEvent {
  seq: number;
  at: number;
  kind: string;
  payload: Record<string, unknown>;
}
export interface GitBranch {
  name: string;
  kind: 'base' | 'integration' | 'member' | 'task' | 'solo' | 'other' | string;
  head?: string;
  ahead?: number;
  behind?: number;
  dirty?: boolean;
  owner?: string;
  teamId?: string;
  unpushed?: number;
  subject?: string;
  lastCommitAt?: number;
  mergedIntoBase?: boolean;
  mergedIntoIntegration?: boolean;
}
export interface GitStatus {
  base?: string;
  integration?: { branch: string; pending: number; exists: boolean };
  dirtyBase?: boolean;
  hasOrigin?: boolean;
  branches?: GitBranch[];
  worktrees?: Array<{ path: string; branch: string; head: string; bare: boolean }>;
}
export interface PullRequest {
  provider: 'github' | 'azure-devops';
  id: string;
  title: string;
  author: { name: string; login?: string };
  source: string;
  target: string;
  state: 'open' | 'draft' | 'merged' | 'closed';
  url: string;
  updatedAt?: string;
  reviewers?: Array<{ name?: string; verdict?: string; isMe?: boolean }>;
  labels?: string[];
  isMine?: boolean;
  needsMyReview?: boolean;
  commentCount?: number;
}
export interface PullRequestsState {
  source?: { provider: string; label: string; webUrl?: string; signedIn: boolean; note?: string };
  pullRequests: PullRequest[];
  note?: string;
}
export interface PipelineRun {
  id: string;
  pipelineId?: string;
  name?: string;
  title?: string;
  status: string;
  conclusion?: string;
  branch?: string;
  webUrl?: string;
  startedAt?: string;
  updatedAt?: string;
  [k: string]: unknown;
}
export interface PipelineSource {
  id: string;
  provider: string;
  label: string;
  webUrl?: string;
  signedIn: boolean;
  pipelines: Array<{ id: string; name: string; path?: string; [k: string]: unknown }>;
  runs: PipelineRun[];
  error?: string;
}
export interface PipelinesState {
  sources: PipelineSource[];
  branch?: string;
  branches: string[];
  note?: string;
}
export interface UsageTotals {
  requests: number;
  promptTokens: number;
  completionTokens: number;
  costUsd: number;
  partialCost?: boolean;
}
export interface Usage {
  days: number;
  totals: UsageTotals;
  daily: Array<{ day: string; totals: UsageTotals }>;
  byRuntime?: Array<{ name: string; totals: UsageTotals }>;
  byAgent?: Array<{ name: string; totals: UsageTotals }>;
  byModel?: Array<{ name: string; totals: UsageTotals }>;
  byProvider?: Array<{ name: string; totals: UsageTotals }>;
}
export interface LocalModel {
  available: boolean;
  endpoint?: string;
  model?: string;
  acceleration?: string;
  runsIn?: string;
  reachable?: boolean;
  reason?: string;
}
export interface Review {
  date: string;
  generatedAt: string;
  summary?: string;
  prescriptions: Array<{ id: string; cat: string; headline: string; prescription: string; status: string; dollarImpact?: number; timeImpactMins?: number; ageDays?: number }>;
}
export interface MemoryStatus {
  available: boolean;
  enabled?: boolean;
  graphExists?: boolean;
  nodes?: number;
  links?: number;
  communities?: number;
  staleFiles?: number;
  graphUpdatedAt?: string;
  engine?: string;
}

export function describeRpcError(e: unknown): string {
  if (e instanceof ApiError) {
    if (e.code === 'offline') return 'The workbench is not connected to the relay right now.';
    if (e.code === 'timeout') return 'The workbench did not answer in time.';
    return `${e.code}: ${JSON.stringify(e.body)}`;
  }
  const m = (e as Error).message;
  return m.includes('-32002') || m.toLowerCase().includes('policy') ? `${m} (the workbench's policy decides what remote clients may approve)` : m;
}

/**
 * What a view reads from the machine besides its own `useView` polls. The page asks only for what the open view
 * shows, so a view that is not open costs the machine nothing; the device record (the platform's, not the
 * machine's) is always read.
 */
export type WorkbenchNeed = 'workspaces' | 'status' | 'activity' | 'events';

/** How often each shared read asks while a view needs it; every poll pauses while the tab is hidden. */
const DEVICE_MS = 30_000;
const ACTIVITY_MS = 30_000;
const STATUS_MS = 60_000;
const WORKSPACES_MS = 120_000;
const EVENTS_MS = 30_000;
const VISIBLE = { whenVisible: true } as const;

/** `relay: false` (AI Cloud's free plan): nothing is asked of the machine at all. */
export function useWorkbench(deviceId: string, needs: readonly WorkbenchNeed[] = [], opts: { relay?: boolean } = {}) {
  const device = useLoad(() => routes.device(deviceId), [deviceId], DEVICE_MS, VISIBLE);
  const connected = opts.relay !== false && (device.data?.relayConnected ?? false);
  const [workspace, setWorkspace] = useState<string>();
  const [error, setError] = useState<string>();
  const wantsWorkspaces = connected && needs.includes('workspaces');
  const wantsStatus = connected && needs.includes('status');
  const wantsActivity = connected && needs.includes('activity');
  const wantsEvents = connected && needs.includes('events');

  const call = useCallback(
    async <T,>(method: string, params?: unknown): Promise<T | undefined> => {
      const res = (await routes.rpc(deviceId, method, params, workspace)) as { result?: T; error?: { code: number; message: string } };
      if (res.error) throw new Error(`${method}: ${res.error.message}`);
      return res.result;
    },
    [deviceId, workspace],
  );

  const workspaces = useLoad<Workspace[] | undefined>(async () => (wantsWorkspaces ? workspaceList(await call<unknown>('workspaces.list')) : undefined), [wantsWorkspaces, deviceId], wantsWorkspaces ? WORKSPACES_MS : undefined, VISIBLE);
  const status = useLoad<Status | undefined>(() => (wantsStatus ? call<Status>('workbench.status') : Promise.resolve(undefined)), [wantsStatus, workspace], wantsStatus ? STATUS_MS : undefined, VISIBLE);
  // Who is working: sessions.list and permissions.list are quick and every workbench has them;
  // agents.activity (the app's own live view: operations manager, tasks, decisions per agent)
  // enriches the picture when the workbench answers it in time.
  const activity = useLoad<Activity | undefined>(
    async () => {
      if (!wantsActivity) return undefined;
      const [sessions, permissions] = await Promise.all([call<Session[]>('sessions.list'), call<Permission[]>('permissions.list').catch(() => [] as Permission[])]);
      const agents: ActivityAgent[] = (sessions ?? []).map((s) => ({
        sessionId: s.sessionId,
        name: s.name,
        status: s.status,
        teamId: s.teamName ?? 'solo',
        provider: s.provider,
        model: s.model,
        title: s.sessionTitle,
        currentTool: s.currentTool,
        lastActivityAt: s.lastActivityAt,
        tasks: [],
        permissions: (permissions ?? []).filter((p) => p.sessionId === s.sessionId),
        decisions: [],
      }));
      const base: Activity = {
        at: Date.now(),
        agents,
        decisions: [],
        working: agents.filter((a) => a.status === 'thinking' || a.status === 'tool_calling').length,
        pendingApprovals: (permissions ?? []).length,
        openDecisions: 0,
      };
      try {
        const rich = await Promise.race([call<Activity>('agents.activity'), new Promise<undefined>((r) => setTimeout(() => r(undefined), 6_000))]);
        if (!rich) return base;
        // Merge: the rich view knows the operations manager, tasks and decisions; keep sessions the rich view lacks.
        const byId = new Map(rich.agents.map((a) => [a.sessionId, a]));
        return {
          ...rich,
          agents: agents.map((a) => ({ ...a, ...(byId.get(a.sessionId) ?? {}) })).concat(rich.agents.filter((a) => !agents.some((b) => b.sessionId === a.sessionId))),
        };
      } catch {
        return base;
      }
    },
    [wantsActivity, workspace],
    wantsActivity ? ACTIVITY_MS : undefined,
    VISIBLE,
  );

  const [events, setEvents] = useState<RingEvent[]>([]);
  const cursor = useRef(0);
  useEffect(() => {
    if (!wantsEvents) return;
    let stop = false;
    const tick = async () => {
      if (document.visibilityState === 'hidden') return;
      try {
        const r = await call<{ events: RingEvent[]; cursor: number }>('events.poll', { cursor: cursor.current, limit: 200 });
        if (stop || !r) return;
        if (r.events.length) {
          cursor.current = r.cursor;
          setEvents((prev) => [...r.events.reverse(), ...prev].slice(0, 500));
        }
      } catch {
        /* the next tick retries */
      }
    };
    void tick();
    const t = setInterval(tick, EVENTS_MS);
    return () => {
      stop = true;
      clearInterval(t);
    };
  }, [wantsEvents, call]);

  async function act(label: string, fn: () => Promise<unknown>, after?: () => void): Promise<boolean> {
    setError(undefined);
    try {
      await fn();
      after?.();
      return true;
    } catch (e) {
      setError(`${label}: ${describeRpcError(e)}`);
      return false;
    }
  }

  /**
   * A method the workbench may not have yet (a newer Control API addition): undefined when it
   * answers "method not found", so a view can show the richer picture as soon as the app gains it.
   */
  const tryCall = useCallback(
    async <T,>(method: string, params?: unknown): Promise<T | undefined> => {
      try {
        return await call<T>(method, params);
      } catch (e) {
        if (/method not found/i.test((e as Error).message)) return undefined;
        throw e;
      }
    },
    [call],
  );

  /** A per-view poll that only runs while the view is mounted: never more often than every 30 seconds, and not while the tab is hidden. */
  function useView<T>(method: string, params: unknown, deps: unknown[], intervalMs: number) {
    return useLoad<T | undefined>(() => (connected ? call<T>(method, params) : Promise.resolve(undefined)), [connected, workspace, ...deps], Math.max(30_000, intervalMs), VISIBLE);
  }

  return { deviceId, device, connected, workspace, setWorkspace, workspaces, status, activity, events, call, tryCall, act, error, setError, useView };
}

export type Workbench = ReturnType<typeof useWorkbench>;
